import {
  formatCfTextValue,
  isFormattedCfType,
} from "@/features/customField/utils/cfValueTypes";

interface CustomInputScope {
  customInputId?: number;
  textValue?: string;
  optionIds?: string[];
  /**
   * Resolved labels for `optionIds`, in the same order, sent by the API
   * (`scopeMap`). Display fallback for when the (per-viewer NARROWED) field
   * option list doesn't contain a value already set on the deal — without it
   * the row showed a raw item id like "644" instead of the country name.
   */
  optionValues?: string[];
}

/** Coerce `optionIds` to strings when hydrating RHF (API sends numeric ids). */
export function normalizeCustomInputScopesForForm(
  scopes: unknown[] | undefined | null,
): any[] {
  if (!Array.isArray(scopes)) return [];
  return scopes
    .filter((s): s is Record<string, unknown> => s != null && typeof s === "object")
    .map((s) => {
      const raw = s.optionIds;
      if (!Array.isArray(raw)) return s as CustomInputScope;
      return {
        ...s,
        optionIds: raw.map((id) => String(id)),
      } as CustomInputScope;
    });
}

export const getCustomFieldValue = (field: any, customInputScopes: any[]) => {
  if (!field || !customInputScopes?.length) return "N/A";

  const safeScopes = (customInputScopes ?? []).filter(
    (item): item is CustomInputScope => item != null
  );
  const scope = safeScopes.find(
    (item) => Number(item?.customInputId) === Number(field.id)
  );

  if (!scope) return "N/A";

  // Number / Percentage / Date keep their value in textValue ("45.5" →
  // "45.5%", "2026-10-31" → "31 Oct 2026"); their only option is the hidden
  // "Text" anchor, never a value.
  if (isFormattedCfType(field.type)) {
    return scope.textValue
      ? formatCfTextValue(field.type, scope.textValue)
      : "N/A";
  }

  const optionIds = (
    Array.isArray(scope.optionIds) ? scope.optionIds : []
  ).map((id: unknown) => String(id));

  /**
   * After TEXT_FIELD → SELECTOR migration, the API stores values on `optionIds`
   * (from `CustomInputItemScope.itemId`) and clears `textValue`. `useSpesificCustoms`
   * can still briefly report `field.type === "TEXT_FIELD"` — always prefer `optionIds`
   * when present so the lead/deal row does not show "N/A".
   */
  if (optionIds.length > 0) {
    const selectedOptions = field.options?.filter((opt: any) => {
      const optId = String(opt.id ?? opt.value ?? "");
      return optionIds.some((id) => id === optId);
    });

    if (selectedOptions?.length) {
      return selectedOptions
        .map((opt: any) => opt.label ?? opt.name ?? opt.value)
        .join(", ");
    }

    // The viewer's option list is NARROWED per-grant (filterCustomFieldOptions),
    // so a value already set on the deal whose option the viewer isn't granted
    // is absent from `field.options` and can't be resolved here. Use the labels
    // the API sent with the scope so we render the country NAME, never a raw id.
    const apiLabels = Array.isArray(scope.optionValues)
      ? scope.optionValues.map((v) => String(v ?? "").trim()).filter(Boolean)
      : [];
    if (apiLabels.length) return apiLabels.join(", ");

    // No label available anywhere — show "N/A" rather than a meaningless id.
    return "N/A";
  }

  if (field.type === "TEXT_FIELD" || field.type === "REMARK") {
    return scope.textValue || "N/A";
  }

  return "N/A";
};
